import React, { useState, useMemo } from 'react';
import { 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  Loader2, 
  ArrowUpDown,
  AlertTriangle,
  CircleDot,
  Eye
} from 'lucide-react';
import TicketDetailModal from '../../components/Support/TicketDetailsModal';

const TicketTable = ({ tickets }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 8;
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Sorting function
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sorted and filtered data
  const sortedTickets = useMemo(() => {
    let filteredData = [...tickets].filter(ticket =>
      Object.values(ticket).some(value => 
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    return filteredData.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [tickets, sortConfig, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(sortedTickets.length / itemsPerPage);
  const paginatedTickets = sortedTickets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Status badge styles with icons
  const getStatusBadgeClass = (status) => {
    const baseClasses = "px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-32 justify-center";
    switch (status.toLowerCase()) {
      case 'resolved':
        return {
          className: `${baseClasses} bg-green-100 text-green-800`,
          icon: <CheckCircle2 size={14} />,
          text: 'Resolved'
        };
      case 'pending':
        return {
          className: `${baseClasses} bg-yellow-100 text-yellow-800`,
          icon: <Clock size={14} />,
          text: 'Pending'
        };
      case 'in-progress':
        return {
          className: `${baseClasses} bg-blue-100 text-blue-800`,
          icon: <Loader2 size={14} className="animate-spin" />,
          text: 'In-Progress'
        };
      case 'not resolved':
        return {
          className: `${baseClasses} bg-red-100 text-red-800`,
          icon: <AlertCircle size={14} />,
          text: 'Not Resolved'
        };
      default:
        return {
          className: `${baseClasses} bg-gray-100 text-gray-800`,
          icon: <CircleDot size={14} />,
          text: status
        };
    }
  };

  // Priority badge styles with icons
  const getPriorityBadgeClass = (priority) => {
    const baseClasses = "px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-32 justify-center";
    switch (priority.toLowerCase()) {
      case 'high':
        return {
          className: `${baseClasses} bg-red-100 text-red-800`,
          icon: <AlertTriangle size={14} />,
          text: 'High'
        };
      case 'medium':
        return {
          className: `${baseClasses} bg-yellow-100 text-yellow-800`,
          icon: <AlertCircle size={14} />,
          text: 'Medium'
        };
      case 'low':
        return {
          className: `${baseClasses} bg-green-100 text-green-800`,
          icon: <CircleDot size={14} />,
          text: 'Low'
        };
      default:
        return {
          className: `${baseClasses} bg-gray-100 text-gray-800`,
          icon: <CircleDot size={14} />,
          text: priority
        };
    }
  };

  return (
    <div className="mt-8">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Ticket List</h2>
          <div className="flex gap-3">
            <input
              type="search"
              placeholder="Search tickets..."
              className="px-4 py-2 border rounded-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="px-4 py-2 bg-[#FF6C6C] text-white rounded-lg">
              Export
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                {[
                  { key: 'id', label: 'Ticket Id' },
                  { key: 'submittedBy', label: 'Submitted By' },
                  { key: 'issueType', label: 'Issue Type' },
                  { key: 'priority', label: 'Priority Level' },
                  { key: 'timestamp', label: 'Timestamp' },
                  { key: 'status', label: 'Status' },
                  { key: 'action', label: 'Action' }
                ].map(({ key, label }) => (
                  <th
                    key={key}
                    onClick={() => key !== 'action' && requestSort(key)}
                    className="px-6 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  >
                    <div className="flex items-center gap-1">
                      {label}
                      {key !== 'action' && <ArrowUpDown size={14} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedTickets.map((ticket) => (
                <tr key={ticket.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 whitespace-nowrap text-sm">{ticket.id}</td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm">{ticket.submittedBy}</td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm">{ticket.issueType}</td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm">
                    {(() => {
                      const { className, icon, text } = getPriorityBadgeClass(ticket.priority);
                      return (
                        <span className={className}>
                          {icon}
                          {text}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm">{ticket.timestamp}</td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm">
                    {(() => {
                      const { className, icon, text } = getStatusBadgeClass(ticket.status);
                      return (
                        <span className={className}>
                          {icon}
                          {text}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm">
                    <button 
                      onClick={() => setSelectedTicket(ticket)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors duration-150 rounded-md text-xs font-medium"
                    >
                      <Eye size={14} />
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <div className="text-xs text-gray-700">
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sortedTickets.length)} of {sortedTickets.length} entries
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 border rounded-md ${
                  currentPage === i + 1 ? 'bg-[#FF6C6C] text-white' : ''
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>

        <TicketDetailModal 
          isOpen={!!selectedTicket}
          onClose={() => setSelectedTicket(null)}
          ticket={selectedTicket}
        />
      </div>
    </div>
  );
};

export default TicketTable;
